<template>
  <div>
    <p class="my-5 txt-body-m-medium">{{ typed_t('ticketForm.paymentDetailsTitle') }}</p>
    <p class="mb-4 text-body-s text-subtle">
      {{ typed_t('ticketForm.paymentDetailsDescription') }}
    </p>

    <div class="mb-2 flex gap-4">
      <label class="flex cursor-pointer items-center gap-2">
        <input
          v-model="business"
          type="radio"
          name="business-type"
          :value="false"
          class="h-4 w-4 cursor-pointer"
          data-testid="iq-pay-private"
        />
        <span class="text-body-m">{{ typed_t('ticketForm.private') }}</span>
      </label>
      <label class="flex cursor-pointer items-center gap-2">
        <input
          v-model="business"
          type="radio"
          name="business-type"
          :value="true"
          class="h-4 w-4 cursor-pointer"
          data-testid="iq-pay-business"
        />
        <span class="text-body-m">{{ typed_t('ticketForm.business') }}</span>
      </label>
    </div>

    <div class="grid grid-cols-2 gap-2">
      <TextInput class="col-span-2" name="company" :dense="true" :label="t('FestivalShop.checkout.company') + (business ? '*' : '')" />
      <TextInput name="street" :dense="true" :label="t('FestivalShop.checkout.street') + '*'" />
      <TextInput name="number" :dense="true" :label="t('FestivalShop.checkout.no') + '*'" />
      <TextInput
        class="col-span-2"
        name="additionalAddress"
        :dense="true"
        :label="t('FestivalShop.checkout.addressAddition') + ' ' + t('FestivalShop.checkout.optional')"
      />
      <TextInput name="zip" :dense="true" :label="t('FestivalShop.checkout.zipCode') + '*'" />
      <TextInput name="city" :dense="true" :label="t('FestivalShop.checkout.city') + '*'" />
      <SelectForOptions
        class="col-span-2"
        name="country"
        :dense="true"
        :label="t('FestivalShop.checkout.country') + '*'"
        :options="countryOptions"
      />
      <TextInput
        class="col-span-2"
        name="vatNumber"
        :dense="true"
        :label="t('FestivalShop.checkout.vatNumber') + ' ' + t('FestivalShop.checkout.optional')"
      />
      <TextInput
        class="col-span-2"
        name="customerReference"
        :dense="true"
        :label="t('FestivalShop.checkout.orderPoNumber') + ' ' + t('FestivalShop.checkout.optional')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { TextInput, SelectForOptions } from '@ramp106/omrjs-vee-validate'
import { toTypedSchema } from '@vee-validate/yup'
import { useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { object, string } from 'yup'

import { useUserPropertiesPreferencesQuery } from '@/gql/myomr'
import typed_translation from '@/helpers/typed_translation'

const { t } = useI18n()

export interface PaymentInfoData {
  business: boolean
  company: string
  street: string
  number: string
  additionalAddress: string
  zip: string
  city: string
  country: string
  vatNumber: string
  customerReference: string
}

const formFieldKeys = [
  'company',
  'street',
  'number',
  'additionalAddress',
  'zip',
  'city',
  'country',
  'vatNumber',
  'customerReference',
] as const

type FormFieldKey = (typeof formFieldKeys)[number]

function extractFormFields(source: PaymentInfoData): Record<FormFieldKey, string> {
  return Object.fromEntries(formFieldKeys.map((key) => [key, source[key]])) as Record<FormFieldKey, string>
}

function buildPaymentInfoData(formValues: Partial<Record<FormFieldKey, string>>, isBusiness: boolean): PaymentInfoData {
  const fields = Object.fromEntries(formFieldKeys.map((key) => [key, formValues[key] ?? '']))
  return { business: isBusiness, ...fields } as PaymentInfoData
}

const props = defineProps<{
  modelValue: PaymentInfoData
  quotaKind: string
}>()

const typed_t = typed_translation('InvitationQuota', props.quotaKind)

const emit = defineEmits<{
  'update:modelValue': [value: PaymentInfoData]
}>()

const business = ref(props.modelValue.business)

const validationSchema = computed(() => {
  const req = t('validations.required')
  return toTypedSchema(
    object().shape({
      company: business.value ? string().required(req) : string(),
      street: string().required(req),
      number: string().required(req),
      additionalAddress: string(),
      zip: string().required(req),
      city: string().required(req),
      country: string().required(req),
      vatNumber: string(),
      customerReference: string(),
    }),
  )
})

const { values, validate, setTouched } = useForm({
  validationSchema,
  initialValues: extractFormFields(props.modelValue),
})

watch(
  values,
  (newValues) => {
    emit('update:modelValue', buildPaymentInfoData(newValues, business.value))
  },
  { deep: true },
)

watch(business, (newBusiness) => {
  emit('update:modelValue', { ...props.modelValue, business: newBusiness })
})

const { result } = useUserPropertiesPreferencesQuery()
const countryOptions = computed(() => {
  if (!result.value) return []
  return result.value.userPropertiesPreferences.countries
    .map((item) => ({ text: item.translation, value: item.code }))
    .sort((a, b) => a.text.localeCompare(b.text))
})

defineExpose({
  validate: async () => {
    const result = await validate()
    if (!result.valid) {
      const touchedFields = Object.fromEntries(Object.keys(result.errors).map((key) => [key, true]))
      setTouched(touchedFields)
    }
    return result.valid
  },
})
</script>
